import type { FormActions } from 'vee-validate'
import type { MaybeRef } from 'vue'
import type { ZodSchema } from 'zod'
import type { FormSchemaCallback } from './useFormSchema'
import { isDeepEqual } from '@antfu/utils'
import { isNuxtError, showError } from 'nuxt/app'
import { computed, nextTick, onMounted, ref, toValue } from 'vue'
import { useFileUpload } from '../../../composables/file'
import { useLocale } from '../../../composables/locale'
import { toast } from '../sonner'
import { useFormSchema } from './useFormSchema'

interface UseFormCallbackOptions<T extends ZodSchema = ZodSchema> {
  callback: ({ values }: any) => Promise<void>
  onError?: (data: { message: string, setErrors: FormActions<Record<string, any>>['setErrors'] }) => Promise<void>
  onAfter?: () => Promise<void>
  successMessage?: MaybeRef<{
    message: string
    description?: string
  } | false>
  validationSchema?: FormSchemaCallback<T>
  values?: MaybeRef<Record<string, any>>
}

const FILE_UPLOAD_ARRAY_NAME_JOIN = ':||:'

export function useFormCallback<T extends ZodSchema = ZodSchema>(options: UseFormCallbackOptions<T>) {
  const { t } = useLocale()
  const uploadFiles = useFileUpload()
  function showBaseError(description?: string) {
    toast.error(t('validation.failingMsg.heading'), {
      description: description || t('validation.failingMsg.description'),
    })
  }
  const formRef = ref()
  const processing = ref(false)
  async function handleSubmit({ values, setErrors, resetForm }: any) {
    try {
      processing.value = true
      await nextTick()

      const rawUploadFiles: {
        name: string
        file: File
      }[] = []
      for (const valueKey in values) {
        const value = values[valueKey]
        if (value) {
          if (Array.isArray(value)) {
            for (const childValueKey in value) {
              const childValue = value[childValueKey]
              if (childValue?.uploadedFile) {
                rawUploadFiles.push({
                  name: `${valueKey}${FILE_UPLOAD_ARRAY_NAME_JOIN}${childValueKey}`,
                  file: childValue.uploadedFile,
                })
                delete childValue.uploadedFile
              }
            }
          }
          else if (value?.uploadedFile) {
            rawUploadFiles.push({
              name: valueKey,
              file: value.uploadedFile,
            })
            delete value.uploadedFile
          }
        }
      }
      if (rawUploadFiles.length) {
        const fileUploadResponse = await uploadFiles(rawUploadFiles, {
          onProgress() {
            // TODO
          },
        })
        fileUploadResponse.forEach((fileUploadResponseItem) => {
          if (fileUploadResponseItem.name.includes(FILE_UPLOAD_ARRAY_NAME_JOIN)) {
            const nameParts = fileUploadResponseItem.name.split(FILE_UPLOAD_ARRAY_NAME_JOIN)
            const fieldName = nameParts[0]
            const fieldIndex = Number(nameParts[1])
            delete fileUploadResponseItem.name
            values[fieldName][fieldIndex] = fileUploadResponseItem
          }
          else {
            values[fileUploadResponseItem.name] = fileUploadResponseItem
          }
        })
        resetForm({
          values,
        }, {
          force: true,
        })
      }

      await options.callback({ values })
      const successMessage = toValue(options.successMessage)
      if (successMessage) {
        toast.success(successMessage.message, {
          description: successMessage.description,
        })
      }
      else if (successMessage !== false) {
        toast.success(t('savedMsg.heading'), {
          description: t('savedMsg.description'),
        })
      }
    }
    catch (err: any) {
      if (isNuxtError(err) && err.status !== 422) {
        showError({ ...err, fatal: true })
        return
      }
      const message = err?.data?.message || ''
      if (err.status === 422) {
        const rawErrors = err?.data?.errors || {}
        const errors: { [key: string]: string } = {}
        for (const rawErrorKey in rawErrors) {
          const rawError = rawErrors[rawErrorKey]
          errors[rawErrorKey] = Array.isArray(rawError) ? rawError[0] : rawError
        }
        setErrors(errors)
      }
      showBaseError(message)
      await options.onError?.({
        message,
        setErrors,
      })
    }
    finally {
      processing.value = false
    }
    await options.onAfter?.()
  }

  async function handleInvalidSubmit() {
    showBaseError()
  }

  const validationSchema = useFormSchema(options?.validationSchema)
  const values = computed((prev) => {
    const newValues = toValue(options.values)
    if (prev && isDeepEqual(prev, newValues)) {
      return prev
    }
    return newValues
  })

  async function submitForm() {
    onMounted(() => {
      const _form: undefined | HTMLFormElement = formRef.value?.$el
      if (_form) {
        _form.requestSubmit()
      }
    })
  }

  return {
    processing,
    handleSubmit,
    handleInvalidSubmit,
    validationSchema,
    values,
    formRef,
    submitForm,
  }
}
